import type { HsDecodedLine, HsDecodedMesh, HsDecodedPointCloud } from '@sibe/hyperscene';

/** Reads the uncompressed binary mesh, line and point cloud payloads of the geometry endpoints. */
export class HsGeometryBinaryDecoder {
  public decodeMeshesBinary(payload: ArrayBuffer): HsDecodedMesh[] {
    if (payload.byteLength === 0) {
      return [];
    }

    const chunks: HsDecodedMesh[] = [];
    const view: DataView = new DataView(payload);
    let offset: number = 0;

    while (offset < payload.byteLength) {
      if (offset + 8 > payload.byteLength) {
        throw new Error('Invalid meshes payload: broken chunk header.');
      }

      const headerSize: number = view.getInt32(offset, true);
      const bodySize: number = view.getInt32(offset + 4, true);
      offset += 8;

      if (headerSize < 0 || bodySize < 0) {
        throw new Error('Invalid meshes payload: negative chunk size.');
      }

      const chunkEnd: number = offset + headerSize + bodySize;
      if (chunkEnd > payload.byteLength) {
        throw new Error('Invalid meshes payload: chunk exceeds payload size.');
      }

      const geometryId: string = this.readGeometryIdFromHeader(view, offset, headerSize, chunkEnd, 'meshes');

      let cursor: number = offset + headerSize;

      const verticesLength: number = this.readInt32(view, cursor, chunkEnd, 'meshes');
      cursor += 4;
      const vertices: number[] = this.readFloat32Array(view, cursor, verticesLength, chunkEnd, 'meshes');
      cursor += verticesLength * 4;

      const normalsLength: number = this.readInt32(view, cursor, chunkEnd, 'meshes');
      cursor += 4;
      const normals: number[] = this.readFloat32Array(view, cursor, normalsLength, chunkEnd, 'meshes');
      cursor += normalsLength * 4;

      const uvLength: number = this.readInt32(view, cursor, chunkEnd, 'meshes');
      cursor += 4;
      const uvs: number[] = this.readFloat32Array(view, cursor, uvLength, chunkEnd, 'meshes');
      cursor += uvLength * 4;

      const indicesLength: number = this.readInt32(view, cursor, chunkEnd, 'meshes');
      cursor += 4;
      const indices: number[] = this.readInt32Array(view, cursor, indicesLength, chunkEnd, 'meshes');
      cursor += indicesLength * 4;

      if (cursor > chunkEnd) {
        throw new Error('Invalid meshes payload: indices data exceeds chunk bounds.');
      }

      chunks.push({
        geometryId,
        canonicalVertexOrder: true,
        vertices,
        normals,
        uvs,
        indices,
      });

      offset = chunkEnd;
    }

    return chunks;
  }

  public decodeLinesBinary(payload: ArrayBuffer): HsDecodedLine[] {
    if (payload.byteLength === 0) {
      return [];
    }

    const chunks: HsDecodedLine[] = [];
    const view: DataView = new DataView(payload);
    let offset: number = 0;

    while (offset < payload.byteLength) {
      if (offset + 8 > payload.byteLength) {
        throw new Error('Invalid lines payload: broken chunk header.');
      }

      const headerSize: number = view.getInt32(offset, true);
      const bodySize: number = view.getInt32(offset + 4, true);
      offset += 8;

      if (headerSize < 0 || bodySize < 0) {
        throw new Error('Invalid lines payload: negative chunk size.');
      }

      const chunkEnd: number = offset + headerSize + bodySize;
      if (chunkEnd > payload.byteLength) {
        throw new Error('Invalid lines payload: chunk exceeds payload size.');
      }

      const geometryId: string = this.readGeometryIdFromHeader(view, offset, headerSize, chunkEnd, 'lines');

      let cursor: number = offset + headerSize;

      const verticesLength: number = this.readInt32(view, cursor, chunkEnd, 'lines');
      cursor += 4;
      const vertices: number[] = this.readFloat32Array(view, cursor, verticesLength, chunkEnd, 'lines');
      cursor += verticesLength * 4;

      if (cursor > chunkEnd) {
        throw new Error('Invalid lines payload: vertices data exceeds chunk bounds.');
      }

      chunks.push({ geometryId, vertices });
      offset = chunkEnd;
    }

    return chunks;
  }

  public decodePointCloudsBinary(payload: ArrayBuffer): HsDecodedPointCloud[] {
    if (payload.byteLength === 0) {
      return [];
    }

    const chunks: HsDecodedPointCloud[] = [];
    const view: DataView = new DataView(payload);
    let offset: number = 0;

    while (offset < payload.byteLength) {
      if (offset + 8 > payload.byteLength) {
        throw new Error('Invalid point clouds payload: broken chunk header.');
      }

      const headerSize: number = view.getInt32(offset, true);
      const bodySize: number = view.getInt32(offset + 4, true);
      offset += 8;

      if (headerSize < 0 || bodySize < 0) {
        throw new Error('Invalid point clouds payload: negative chunk size.');
      }

      const chunkEnd: number = offset + headerSize + bodySize;
      if (chunkEnd > payload.byteLength) {
        throw new Error('Invalid point clouds payload: chunk exceeds payload size.');
      }

      const geometryId: string = this.readGeometryIdFromHeader(view, offset, headerSize, chunkEnd, 'point clouds');

      let cursor: number = offset + headerSize;

      const pointsLength: number = this.readInt32(view, cursor, chunkEnd, 'point clouds');
      cursor += 4;
      const points: number[] = this.readFloat32Array(view, cursor, pointsLength, chunkEnd, 'point clouds');
      cursor += pointsLength * 4;

      const pointsCount: number = pointsLength / 3;
      if (!Number.isInteger(pointsCount)) {
        throw new Error('Invalid point clouds payload: points length is not divisible by 3.');
      }

      const colors: number[] = this.readInt32Array(view, cursor, pointsCount, chunkEnd, 'point clouds');
      cursor += pointsCount * 4;

      const collisionsLength: number = pointsCount;
      if (cursor + collisionsLength > chunkEnd) {
        throw new Error('Invalid point clouds payload: collisions data exceeds chunk bounds.');
      }

      const collisions: number[] = new Array<number>(pointsCount);
      for (let i: number = 0; i < pointsCount; i++) {
        collisions[i] = view.getUint8(cursor + i);
      }
      cursor += collisionsLength;

      chunks.push({ geometryId, points, colors, collisions });
      offset = chunkEnd;
    }

    return chunks;
  }

  private readInt32(view: DataView, offset: number, boundary: number, payloadType: string): number {
    if (offset + 4 > boundary) {
      throw new Error(`Invalid ${payloadType} payload: unexpected end of integer field.`);
    }

    return view.getInt32(offset, true);
  }

  private readFloat32Array(
    view: DataView,
    offset: number,
    length: number,
    boundary: number,
    payloadType: string
  ): number[] {
    if (length < 0) {
      throw new Error(`Invalid ${payloadType} payload: negative float array length.`);
    }

    const byteLength: number = length * 4;
    if (offset + byteLength > boundary) {
      throw new Error(`Invalid ${payloadType} payload: float array exceeds chunk bounds.`);
    }

    const values: number[] = new Array<number>(length);
    for (let i: number = 0; i < length; i++) {
      values[i] = view.getFloat32(offset + i * 4, true);
    }

    return values;
  }

  private readInt32Array(
    view: DataView,
    offset: number,
    length: number,
    boundary: number,
    payloadType: string
  ): number[] {
    if (length < 0) {
      throw new Error(`Invalid ${payloadType} payload: negative int array length.`);
    }

    const byteLength: number = length * 4;
    if (offset + byteLength > boundary) {
      throw new Error(`Invalid ${payloadType} payload: int array exceeds chunk bounds.`);
    }

    const values: number[] = new Array<number>(length);
    for (let i: number = 0; i < length; i++) {
      values[i] = view.getInt32(offset + i * 4, true);
    }

    return values;
  }

  private readGeometryIdFromHeader(
    view: DataView,
    headerOffset: number,
    headerSize: number,
    chunkEnd: number,
    payloadType: string
  ): string {
    if (headerSize < 16) {
      throw new Error(`Invalid ${payloadType} payload: header is too small to contain geometry id.`);
    }

    const idEnd: number = headerOffset + 16;
    if (idEnd > chunkEnd) {
      throw new Error(`Invalid ${payloadType} payload: geometry id exceeds chunk bounds.`);
    }

    const bytes: number[] = new Array<number>(16);
    for (let i: number = 0; i < 16; i++) {
      bytes[i] = view.getUint8(headerOffset + i);
    }

    return this.formatGuidBytes(bytes);
  }

  private formatGuidBytes(bytes: number[]): string {
    const hex: string[] = bytes.map((value: number) => value.toString(16).padStart(2, '0'));
    return [
      `${hex[0]}${hex[1]}${hex[2]}${hex[3]}`,
      `${hex[4]}${hex[5]}`,
      `${hex[6]}${hex[7]}`,
      `${hex[8]}${hex[9]}`,
      `${hex[10]}${hex[11]}${hex[12]}${hex[13]}${hex[14]}${hex[15]}`,
    ].join('-');
  }
}
